import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const key = new PluginKey('heldSelection');

/**
 * Keeps the selected text visibly highlighted while the editor does not have focus.
 *
 * The browser only paints a selection in the element that is focused, so selecting a
 * word and then clicking into the field-name box makes the highlight vanish, even
 * though the editor still remembers the selection (and will still replace it). While
 * the editor is blurred this draws the same highlight itself, so you can see what the
 * field you are naming will replace. Nothing is drawn while the editor is focused —
 * the browser's own highlight is used then.
 */
export const HeldSelection = Extension.create({
  name: 'heldSelection',

  addProseMirrorPlugins() {
    // The live editor view, so we can ask whether it really has focus rather than trust
    // that we saw every focus/blur event (it may already have been focused when we started).
    let currentView = null;
    return [
      new Plugin({
        key,
        view(view) {
          currentView = view;
          return { destroy: () => { currentView = null; } };
        },
        state: {
          init: () => ({ focused: false }),
          apply: (tr, value) => {
            const meta = tr.getMeta(key);
            return meta ? { focused: meta.focused } : value;
          },
        },
        props: {
          decorations(state) {
            const focused = currentView ? currentView.hasFocus() : key.getState(state).focused;
            const { from, to, empty, node } = state.selection;
            // A selected node (an image) has its own outline, and an empty selection is just a cursor.
            if (focused || empty || node) return DecorationSet.empty;
            return DecorationSet.create(state.doc, [Decoration.inline(from, to, { class: 'held-selection' })]);
          },
          handleDOMEvents: {
            focus(view) {
              view.dispatch(view.state.tr.setMeta(key, { focused: true }));
              return false;
            },
            blur(view) {
              view.dispatch(view.state.tr.setMeta(key, { focused: false }));
              return false;
            },
          },
        },
      }),
    ];
  },
});

export default HeldSelection;
